/**
 * Income-statement document model.
 *
 * Three layers live here:
 *
 * 1. The **native income-statement types** (IFRS SMEs Section 5), moved
 *    verbatim out of `apps/web/src/hooks/useProfitLossData.ts` so the
 *    server-side assembler and the on-screen report share one contract. That
 *    hook now re-exports them, so every existing web importer is untouched.
 * 2. The **translated income-statement types** (IFRS SME Section 30 — foreign
 *    currency translation), moved verbatim out of
 *    `apps/web/src/hooks/useTranslatedIncomeStatement.ts`, which likewise
 *    re-exports them.
 * 3. The assembled **IncomeStatementDocument** — everything a template prints,
 *    fully resolved: the template fetches nothing and derives nothing. That
 *    includes the report title, which used to be computed inline in
 *    `IncomeStatementPreview` (`buildIncomeStatementTitle` below is now called
 *    by both the preview and this model).
 *
 * Each interface is mirrored by a zod schema carrying a
 * `satisfies z.ZodType<...>` guard: the hand-written interface stays the source
 * of truth (it keeps the doc comments the moved code depends on) and the guard
 * turns any drift between the two into a compile error — the same technique
 * `../balanceSheet/model.ts` uses.
 */
import { z } from "zod";
import { documentMetaSchema, orgIdentityBlockSchema, } from "../common.js";
import { translationExchangeRateSchema, } from "../balanceSheet/model.js";
export const expenseNatureSchema = z.enum([
    "cost_of_sales",
    "employee_benefits",
    "depreciation_amortization",
    "raw_materials",
    "finance_costs",
    "tax_expense",
    "other",
]);
/** Recursive (`subaccounts`), so the schema needs the lazy + annotation form. */
export const profitLossItemSchema = z.lazy(() => z.object({
    id: z.string(),
    name: z.string(),
    amount: z.number(),
    currency: z.string(),
    isParent: z.boolean().optional(),
    subaccounts: z.array(profitLossItemSchema).optional(),
    accountCode: z.string().optional(),
    directAmount: z.number().optional(),
    totalAmount: z.number().optional(),
    typeName: z.string().optional(),
    natureClassification: expenseNatureSchema.optional(),
}));
const multiCurrencyTotalSchema = z.record(z.string(), z.number());
/** Zod mirror of `IncomeStatementData` (the `native` block of a document). */
export const incomeStatementDataSchema = z.object({
    revenue: z.array(profitLossItemSchema),
    expenses: z.array(profitLossItemSchema),
    otherIncomeExpense: z.array(profitLossItemSchema),
    totalRevenue: z.number(),
    totalExpenses: z.number(),
    netIncome: z.number(),
    totalRevenueByCurrency: multiCurrencyTotalSchema,
    totalExpensesByCurrency: multiCurrencyTotalSchema,
    netIncomeByCurrency: multiCurrencyTotalSchema,
    costOfSales: z.array(profitLossItemSchema),
    totalCostOfSales: z.number(),
    totalCostOfSalesByCurrency: multiCurrencyTotalSchema,
    grossProfit: z.number(),
    grossProfitByCurrency: multiCurrencyTotalSchema,
    expensesByNature: z.object({
        employeeBenefits: z.array(profitLossItemSchema),
        depreciationAmortization: z.array(profitLossItemSchema),
        rawMaterials: z.array(profitLossItemSchema),
        otherExpenses: z.array(profitLossItemSchema),
    }),
    totalOperatingExpenses: z.number(),
    totalOperatingExpensesByCurrency: multiCurrencyTotalSchema,
    operatingProfit: z.number(),
    operatingProfitByCurrency: multiCurrencyTotalSchema,
    financeCosts: z.array(profitLossItemSchema),
    totalFinanceCosts: z.number(),
    totalFinanceCostsByCurrency: multiCurrencyTotalSchema,
    taxExpense: z.array(profitLossItemSchema),
    totalTaxExpense: z.number(),
    totalTaxExpenseByCurrency: multiCurrencyTotalSchema,
    profitBeforeTax: z.number(),
    profitBeforeTaxByCurrency: multiCurrencyTotalSchema,
    profitForPeriod: z.number(),
    profitForPeriodByCurrency: multiCurrencyTotalSchema,
});
// Empty IFRS-compliant data structure
export const emptyIncomeStatementData = {
    revenue: [],
    expenses: [],
    otherIncomeExpense: [],
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    totalRevenueByCurrency: {},
    totalExpensesByCurrency: {},
    netIncomeByCurrency: {},
    // IFRS additions
    costOfSales: [],
    totalCostOfSales: 0,
    totalCostOfSalesByCurrency: {},
    grossProfit: 0,
    grossProfitByCurrency: {},
    expensesByNature: {
        employeeBenefits: [],
        depreciationAmortization: [],
        rawMaterials: [],
        otherExpenses: [],
    },
    totalOperatingExpenses: 0,
    totalOperatingExpensesByCurrency: {},
    operatingProfit: 0,
    operatingProfitByCurrency: {},
    financeCosts: [],
    totalFinanceCosts: 0,
    totalFinanceCostsByCurrency: {},
    taxExpense: [],
    totalTaxExpense: 0,
    totalTaxExpenseByCurrency: {},
    profitBeforeTax: 0,
    profitBeforeTaxByCurrency: {},
    profitForPeriod: 0,
    profitForPeriodByCurrency: {},
};
/** Recursive (`subaccounts`), so the schema needs the lazy + annotation form. */
export const translatedIncomeStatementItemSchema = z.lazy(() => z.object({
    name: z.string(),
    id: z.string().optional(),
    nativeAmount: z.number(),
    nativeCurrency: z.string(),
    translatedAmount: z.number(),
    presentationCurrency: z.string(),
    exchangeRate: z.number(),
    rateDate: z.string(),
    accountCode: z.string().optional(),
    isParent: z.boolean().optional(),
    translatedDirectAmount: z.number().optional(),
    translatedTotalAmount: z.number().optional(),
    subaccounts: z.array(translatedIncomeStatementItemSchema).optional(),
    typeName: z.string().optional(),
    natureClassification: z.string().optional(),
}));
export const incomeStatementTranslationDisclosuresSchema = z.object({
    exchangeRatesUsed: z.array(translationExchangeRateSchema),
    translationMethod: z.union([z.literal("closing_rate"), z.literal("average_rate")]),
    functionalCurrencies: z.array(z.string()),
    presentationCurrency: z.string(),
    closingDate: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    methodologyNote: z.string(),
});
export const translatedIncomeStatementDataSchema = z.object({
    presentationCurrency: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    revenue: z.array(translatedIncomeStatementItemSchema),
    totalRevenue: z.number(),
    costOfSales: z.array(translatedIncomeStatementItemSchema),
    totalCostOfSales: z.number(),
    grossProfit: z.number(),
    expensesByNature: z.object({
        employeeBenefits: z.array(translatedIncomeStatementItemSchema),
        depreciationAmortization: z.array(translatedIncomeStatementItemSchema),
        rawMaterials: z.array(translatedIncomeStatementItemSchema),
        otherExpenses: z.array(translatedIncomeStatementItemSchema),
    }),
    totalOperatingExpenses: z.number(),
    operatingProfit: z.number(),
    financeCosts: z.array(translatedIncomeStatementItemSchema),
    totalFinanceCosts: z.number(),
    profitBeforeTax: z.number(),
    taxExpense: z.array(translatedIncomeStatementItemSchema),
    totalTaxExpense: z.number(),
    profitForPeriod: z.number(),
    disclosures: incomeStatementTranslationDisclosuresSchema,
    sourceData: incomeStatementDataSchema,
});
/**
 * The report title — extracted from `IncomeStatementPreview.getReportTitle()`
 * so the screen and the PDF print the same heading from one implementation.
 *
 * Behaviour is preserved exactly, including the deliberate omission for the
 * project view (its columnar headers / project cards carry the context).
 */
export function buildIncomeStatementTitle({ orgName, viewBy, fundIdCount, }) {
    const resolvedOrgName = orgName || '';
    const baseTitle = resolvedOrgName ? `${resolvedOrgName} Income Statement` : 'Income Statement';
    if (viewBy === 'unallocated') {
        return `${baseTitle} — Unallocated Transactions`;
    }
    // For project view: just show base title (columnar headers or cards provide context)
    if (viewBy === 'project') {
        return baseTitle;
    }
    if (viewBy === 'funding_source' && fundIdCount) {
        const count = fundIdCount;
        return count === 1
            ? `${baseTitle} — Funding Source View`
            : `${baseTitle} — ${count} Funding Sources`;
    }
    return baseTitle;
}
/**
 * The title variant an assembled document calls for.
 *
 * `IncomeStatementDocument.title` is English by construction — it is built once
 * at assembly time and is what the PDF FILENAME is slugged from. A template
 * that must print a heading in the reader's language therefore cannot use it:
 * it asks here which of the translated title keys applies, and composes the
 * heading from the `documents` i18n namespace (the org name is printed
 * separately by `DocReportHeader`). Keeping the branching here means the
 * template never re-derives what the model already decided.
 */
export function incomeStatementTitleVariant(model) {
    const { projectIds, fundIds, unallocatedOnly } = model.filters;
    const viewBy = deriveIncomeStatementViewBy({ projectIds, fundIds, unallocatedOnly });
    if (viewBy === "unallocated")
        return "unallocated";
    if (viewBy === "project")
        return "project";
    // The `fundIds.length` guard is redundant against the derivation above and
    // deliberately kept: it is the branch `buildIncomeStatementTitle` has, and a
    // funding lens with nothing selected must fall through to the bare title.
    if (viewBy === "funding_source" && fundIds.length) {
        return fundIds.length === 1 ? "fundingSourceView" : "fundingSources";
    }
    return "standard";
}
/**
 * The `viewBy` lens implied by a set of document filters.
 *
 * On screen `viewBy` is an explicit radio the user picks; on the wire it is
 * dropped as derivable (see the plan's "Reconciled API surface"), so the
 * assembler reconstructs it from the filters in the same precedence the
 * preview's title uses: unallocated wins, then projects, then funds.
 */
export function deriveIncomeStatementViewBy({ projectIds, fundIds, unallocatedOnly, }) {
    if (unallocatedOnly)
        return 'unallocated';
    if (projectIds.length > 0)
        return 'project';
    if (fundIds.length > 0)
        return 'funding_source';
    return 'organization';
}
export const incomeStatementCurrencyDisplaySchema = z.enum([
    "translated",
    "multi_currency",
]);
export const incomeStatementDocumentFiltersSchema = z.object({
    projectIds: z.array(z.string()),
    fundIds: z.array(z.string()),
    unallocatedOnly: z.boolean(),
    currencyDisplay: incomeStatementCurrencyDisplaySchema,
});
export const incomeStatementPeriodSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
});
export const incomeStatementProjectRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    budget: z.number().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
});
export const multiProjectIncomeStatementBlockSchema = z.object({
    projects: z.array(incomeStatementProjectRefSchema),
    byProject: z.record(z.string(), incomeStatementDataSchema),
    aggregate: incomeStatementDataSchema,
});
export const incomeStatementRatesStatusSchema = z.object({
    allRatesFound: z.boolean(),
    missingCurrencies: z.array(z.string()),
});
export const incomeStatementComparativeBlockSchema = z.object({
    period: incomeStatementPeriodSchema,
    native: incomeStatementDataSchema,
    translated: translatedIncomeStatementDataSchema,
    ratesStatus: incomeStatementRatesStatusSchema,
});
export const incomeStatementComparativeStatusSchema = z.enum([
    "included",
    "suppressed",
    "unavailable",
]);
export const incomeStatementDocumentSchema = z.object({
    report: z.literal("profit-loss"),
    meta: documentMetaSchema,
    org: orgIdentityBlockSchema,
    period: incomeStatementPeriodSchema,
    filters: incomeStatementDocumentFiltersSchema,
    title: z.string(),
    native: incomeStatementDataSchema,
    translated: translatedIncomeStatementDataSchema.nullable(),
    multiProject: multiProjectIncomeStatementBlockSchema.nullable(),
    ratesStatus: incomeStatementRatesStatusSchema,
    priorPeriod: incomeStatementPeriodSchema.nullable(),
    comparativeStatus: incomeStatementComparativeStatusSchema,
    comparative: incomeStatementComparativeBlockSchema.nullable(),
});
//# sourceMappingURL=model.js.map